import React, { useContext, useState, useEffect, useRef, Fragment } from 'react';
import { VideoStreamRenderer } from '@azure/communication-calling';
import Alert from './Alert';
import { useTranslation } from 'react-i18next';
import useLog from '../hooks/useLog';
import { ACSContext } from '../contexts/ACSContext';
import useACS from '../hooks/useACS';
import './VideoCallWindow.css';
import { Stack, Button, Typography } from '@mui/material';
import AudioPlayer from './AudioPlayer';

const VideoCallWindow = () => {
const { t } = useTranslation();
const { LogInfo } = useLog();
const { activeCall, incomingCall, deviceManager, remoteVideoStream, videoAvailable, setSelectedDevice, remoteName } = useContext(ACSContext);
const { acceptCall, endCall } = useACS();
const [error, setError] = useState(null);
const videoRef = useRef(null);
const [isAlertOpen, setIsAlertOpen] = useState(false);
const [callConnected, setCallConnected] = useState(false);

const [selectedAudioDevice, setSelectedAudioDevice] = useState({
  microphone: null,
  speaker: null 
});

const [audioDevices, setAudioDevices] = useState({
  microphones: [],
  speakers: []
});

var remoteVideoContainer = undefined;
var view = undefined;

const [incomingRingtoneActive, setIncomingRingtoneActive] = useState(false);
const [outgoingRingtoneActive, setOutgoingRingtoneActive] = useState(false);

//when component is unmounted, stop the ringtone
useEffect(() => {
  return () => {
    setIncomingRingtoneActive(false);
    setOutgoingRingtoneActive(false);
  }
},[]);

useEffect(() => {

  const handleStateChange = () => {
    LogInfo('VideoCallWindow - Call state changed:', activeCall.state);
    if(activeCall.state==="Ringing" || activeCall.state==="Connecting"){
      setOutgoingRingtoneActive(true);
    }
    else
    {
      setOutgoingRingtoneActive(false);
    }
    if(activeCall.state==="Connected"){
      setCallConnected(true);
    }
    else {
      setCallConnected(false);
    }

    if(activeCall.state==="Disconnected"){
      //tear down activeCall/incomingCall/remoteName in context immediately, rather than
      //waiting on useACS's own 'stateChanged' listener, which can lag behind this one
      //(e.g. this listener already hides the audio graphic on the earlier "Disconnecting" state)
      endCall();
    }

  };

  if(activeCall) {
    activeCall.on('stateChanged', handleStateChange);  
  }
  return () => {
    if(activeCall) {
      activeCall.off('stateChanged', handleStateChange);  
    }
  }
},[activeCall]);

useEffect(() => {
  if (incomingCall) {
    setIncomingRingtoneActive(true);
  } 
  else{
    setIncomingRingtoneActive(false);
  }
}, [incomingCall]);

useEffect(() => {
  const startVideo = async () => {
    try {
      remoteVideoContainer = document.createElement('div');
      remoteVideoContainer.className = 'video-container';
      var renderer = new VideoStreamRenderer(remoteVideoStream);
      view = await renderer.createView({ scalingMode: 'Crop' }); 
      remoteVideoContainer.appendChild(view.target);
      videoRef.current.appendChild(remoteVideoContainer);
      } catch (err) {
      console.error('Error setting up video stream:', err);
      setError('Failed to setup video stream');
    }
  }

  if (videoAvailable && remoteVideoStream){
    startVideo();
  }
  else{
    LogInfo('VideoCallWindow - No video stream');
  } 

},[remoteVideoStream]);


  const fetchDevices = async () => {
    if (!deviceManager) {
        console.error("Device manager is not available");
        setError("Device manager is not available");
        return;
    }
    try {
        const microphones = await deviceManager.getMicrophones();
        const speakers = await deviceManager.getSpeakers();
        const selectedMicrophone = deviceManager.selectedMicrophone;
        const selectedSpeaker = deviceManager.selectedSpeaker;
        setAudioDevices({ microphones, speakers });
        setSelectedAudioDevice({
          microphone: selectedMicrophone?.id || null,
          speaker: selectedSpeaker?.id || null
      });
    } catch (error) {
        console.error("Error fetching devices:", error);
        setError("Failed to fetch audio devices");
    }
};

  useEffect(() => {
      if (deviceManager) {
        fetchDevices();
      }

      const handleAudioDevicesUpdated = async () => {
          console.log("Audio devices updated");
          await fetchDevices();
      };
      if (deviceManager) {
        deviceManager.on('audioDevicesUpdated', handleAudioDevicesUpdated);
      }

      return () => {
        if (deviceManager) {
          deviceManager.off('audioDevicesUpdated', handleAudioDevicesUpdated);
        }
      };
  }, [deviceManager]);

// Handle selecting a device
const handleDeviceSelect = (deviceId) => {
  updateSelectedDevice(deviceId);
  setSelectedDevice(deviceId);
  setIsAlertOpen(false);
};
const updateSelectedDevice = async (deviceId) => {
  const selectedDevice = [
    ...audioDevices.microphones,
    ...audioDevices.speakers
  ].find(device => device.id === deviceId);

  if (!selectedDevice) {
    console.error("Device not found");
    return;
  }


  try {
    let selectedDeviceType = null;

    if (audioDevices.microphones.some(device => device.id === deviceId)) {
      await deviceManager.selectMicrophone(selectedDevice);
      console.log("Microphone selected:", selectedDevice);
      selectedDeviceType = 'microphone';
    } else if (audioDevices.speakers.some(device => device.id === deviceId)) {
      await deviceManager.selectSpeaker(selectedDevice);
      console.log("Speaker selected:", selectedDevice);
      selectedDeviceType = 'speaker';
    }
  
    if (selectedDeviceType) {
      setSelectedAudioDevice({
        microphone: selectedDeviceType === 'microphone' ? selectedDevice?.id || null : null,
        speaker: selectedDeviceType === 'speaker' ? selectedDevice?.id || null : null
      });
    }
  } catch (error) {
    console.error("Error selecting device:", error);
  }
};

// Handle closing the alert
const handleCloseAlert = () => {
  setIsAlertOpen(false);
};

const handleAcceptCall = () => {
    if (incomingCall) {
      setIncomingRingtoneActive(false);
      acceptCall();
    } else {
      console.error("No incoming call to accept");
    }
  }

const handleEndCall = () => {
    setIncomingRingtoneActive(false);
    setOutgoingRingtoneActive(false);
    endCall();
  }

  const showConnectedAudioDevices = () => {
    if (!isAlertOpen) return null;

    return (
      <Alert
      audioDevices={audioDevices}
      selectedDevice={selectedAudioDevice}
      onSelect={handleDeviceSelect}
      onClose={handleCloseAlert}
  />
    );
};

  if (!incomingCall && !activeCall) {
    return null; // Do not render anything if no call is happening
  }

  return (
    <Stack direction="column" sx={{m:2, justifyContent: 'center', alignItems: 'center'}} spacing={2}>
      <Stack direction="row">
        <Typography variant="h5" sx={{m:1}} color='white'>{incomingCall?t('call_from'):t('call_with')}</Typography>
        <Typography variant="h5" sx={{m:1}} color='white'> {remoteName && remoteName.displayName}</Typography>
      </Stack>
      {remoteName && remoteName.deviceType==="phone" &&
      <Stack direction="row" sx={{m:1}} spacing={1}>
        <Typography variant="h5" color='white'>{t('label_flat')}: &nbsp;</Typography>
        <Typography variant="h5" color='white'>{remoteName && remoteName.familyName}&nbsp;({remoteName && remoteName.flatNumber})</Typography>
      </Stack>
      }
      {remoteName && remoteName.buildingName &&
      <Stack direction="row" sx={{m:1}} spacing={1}>
        <Typography variant="h5" color='white'>{t('label_building')}: &nbsp;</Typography>
        <Typography variant="h5" color='white'>{remoteName && remoteName.buildingName}</Typography>
      </Stack>}
      <Stack direction="row" id="buttons">
        {incomingCall && 
        <Button variant="contained" color="success" onClick={handleAcceptCall} sx={{m:1}}>{t('accept_call')}</Button>}
        <Button variant="contained" color="error" onClick={handleEndCall} sx={{m:1}}>{t('end_call')}</Button>
        <Button variant="contained" onClick={() => {setIsAlertOpen(true);}} sx={{m:1}}>  {t('audio_device')}</Button>
      </Stack>
      <Fragment>
        { error ? (
          <div className="error-message">
            <p>{error}</p>
          </div>
        ) : videoAvailable  ? (
          <div ref={videoRef} />
        ) : (
          callConnected && 
          <img src="../../img/audio-wave.webp" alt="Audio only" style={{maxWidth: '50%'}} />
        )}
        <div id="audio-player">
          <AudioPlayer src="../../snd/ring.mp3" active={incomingRingtoneActive} />
          <AudioPlayer src="../../snd/call.mp3" active={outgoingRingtoneActive} />  
        </div>
      </Fragment>
      <div>
      {showConnectedAudioDevices()}
      </div>
    </Stack>
  );
};

export default VideoCallWindow;